import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Music, ArrowRight, Loader2, QrCode, Hash, Gamepad2, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

export default function BingoJoinPage() {
  const navigate = useNavigate();
  const [gameCode, setGameCode] = useState('');
  const [checking, setChecking] = useState(false);

  const handleJoin = async () => {
    const code = gameCode.trim().toUpperCase();
    
    if (!code || code.length < 4) {
      toast.error('Please enter a valid game code');
      return;
    }
    
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${code}`);
      
      if (res.ok) {
        const game = await res.json();
        if (game.status === 'ended') {
          toast.error('This game has ended');
        } else {
          // Route based on game type
          if (game.game_type === 'uk90') {
            navigate(`/bingo/uk90/play/${code}`);
          } else {
            navigate(`/bingo/play/${code}`);
          }
        }
      } else {
        toast.error('Game not found');
      }
    } catch (error) {
      toast.error('Failed to check game');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-900/30 to-slate-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-600/30">
            <Gamepad2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Bingo Games</h1>
          <p className="text-slate-400">Choose your game type or enter a code to join</p>
        </div>

        {/* Join Game Card */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-violet-400" />
              Join a Game
            </h2>
            <div className="flex gap-3">
              <Input
                value={gameCode}
                onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                placeholder="Enter game code"
                className="bg-slate-800 border-slate-700 text-white text-center text-2xl font-mono h-14 tracking-widest flex-1"
                maxLength={6}
                onKeyPress={(e) => e.key === 'Enter' && handleJoin()}
              />
              <Button
                onClick={handleJoin}
                disabled={checking || !gameCode.trim()}
                className="bg-violet-600 hover:bg-violet-500 h-14 px-6"
              >
                {checking ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Join
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Game Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Line Dance Bingo */}
          <Card className="bg-slate-900 border-slate-800 hover:border-violet-500/50 transition-colors group">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-violet-600 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform mb-4">
                  <Music className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Line Dance Bingo</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Bingo with dance names! Perfect for line dance socials.
                </p>
                <ul className="text-xs text-slate-400 mb-4 space-y-1 text-left">
                  <li>• 3×5 card with dance names</li>
                  <li>• Line or Full House wins</li>
                  <li>• OGNIB reverse mode</li>
                </ul>
                <Link to="/bingo/host" className="w-full">
                  <Button className="w-full bg-violet-600 hover:bg-violet-500">
                    Host Game
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* UK 90-Ball Bingo */}
          <Card className="bg-slate-900 border-slate-800 hover:border-amber-500/50 transition-colors group">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-amber-600 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform mb-4">
                  <Hash className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">UK 90-Ball Bingo</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Traditional British bingo with classic calls.
                </p>
                <ul className="text-xs text-slate-400 mb-4 space-y-1 text-left">
                  <li>• 9×3 UK ticket format</li>
                  <li>• 1-6 tickets per player</li>
                  <li>• Progressive patterns</li>
                </ul>
                <Link to="/bingo/uk90/host" className="w-full">
                  <Button className="w-full bg-amber-600 hover:bg-amber-500">
                    Host Game
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Raffle Draw */}
          <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-colors group">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform mb-4">
                  <Ticket className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Raffle Draw</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Random number generator for raffle tickets.
                </p>
                <ul className="text-xs text-slate-400 mb-4 space-y-1 text-left">
                  <li>• Set ticket number range</li>
                  <li>• Animated spinning draw</li>
                  <li>• TV display for big screens</li>
                </ul>
                <Link to="/raffle/host" className="w-full">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-500">
                    Host Raffle
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Footer */}
        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>Scan a QR code from a host to join their game, or create your own!</p>
        </div>
      </div>
    </div>
  );
}
